/** The now-playing bar: what is on the platter, and the controls for it. */

import { type Component, createEffect } from 'solid-js';
import { artwork, track } from '../../state/player.ts';
import type { Artwork } from '../../types/media.ts';
import glass from '../primitives/Glass.module.css';
import { Extras } from './Extras.tsx';
import styles from './NowPlaying.module.css';
import { Scrubber } from './Scrubber.tsx';
import { Transport } from './Transport.tsx';

const ART_SIZE = 140;

/** Paints the cover into the thumbnail, centre-cropped to fill it. */
function paintArtwork(canvas: HTMLCanvasElement, art: Artwork | null): void {
  const g = canvas.getContext('2d');
  if (!g) return;
  g.clearRect(0, 0, canvas.width, canvas.height);

  const image = art?.image;
  if (!image) {
    g.fillStyle = '#E4EDE8';
    g.fillRect(0, 0, canvas.width, canvas.height);
    return;
  }

  try {
    const iw = 'naturalWidth' in image ? image.naturalWidth : (image as HTMLCanvasElement).width;
    const ih = 'naturalHeight' in image ? image.naturalHeight : (image as HTMLCanvasElement).height;
    const s = Math.max(canvas.width / iw, canvas.height / ih);
    g.drawImage(image, (canvas.width - iw * s) / 2, (canvas.height - ih * s) / 2, iw * s, ih * s);
  } catch {
    /* A tainted or half-decoded image still gets its dominant colour. */
    g.fillStyle = art?.palette.dominant ?? '#2FBF71';
    g.fillRect(0, 0, canvas.width, canvas.height);
  }
}

const subtitle = (): string => {
  const t = track();
  if (!t) return 'Open the library to drop a record';
  return [t.artist, t.album].filter(Boolean).join(' · ');
};

export const NowPlaying: Component = () => {
  let canvas!: HTMLCanvasElement;

  createEffect(
    () => artwork(),
    (art) => {
      paintArtwork(canvas, art);
    },
  );

  return (
    <div class={[glass.glass, styles.np]} data-nowplaying>
      <div class={styles.art}>
        <canvas ref={canvas} width={ART_SIZE} height={ART_SIZE} />
      </div>

      <div class={styles.main}>
        <div class={styles.meta}>
          <div class={styles.title} data-testid="np-title">
            {track()?.title ?? 'No record loaded'}
          </div>
          <div class={styles.artist} data-testid="np-artist">
            {subtitle()}
          </div>
        </div>
        <Scrubber />
      </div>

      <Transport />
      <Extras />
    </div>
  );
};
